import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Input from '../UI/Input';
import Badge from '../UI/Badge';

export default function QuizBuilder() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    timeLimit: '',
  });
  const [questions, setQuestions] = useState([
    { question: '', options: ['', '', '', ''], correctAnswer: 0 }
  ]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addQuestion = () => {
    setQuestions([...questions, { question: '', options: ['', '', '', ''], correctAnswer: 0 }]);
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...questions];
    updated[questionIndex].options[optionIndex] = value;
    setQuestions(updated);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = '퀴즈 제목을 입력해주세요.';
    }

    if (questions.length === 0) {
      newErrors.questions = '최소 1개의 문제를 추가해주세요.';
    }

    questions.forEach((q, i) => {
      if (!q.question.trim()) {
        newErrors[`question_${i}`] = '문제를 입력해주세요.';
      }
      if (q.options.some(opt => !opt.trim())) {
        newErrors[`options_${i}`] = '모든 선택지를 입력해주세요.';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      const payload = {
        ...formData,
        courseId: parseInt(courseId),
        questions,
        timeLimit: formData.timeLimit ? parseInt(formData.timeLimit) : null,
      };

      const response = await api.post('/api/quizzes', payload);

      if (response.data.success) {
        navigate(`/courses/${courseId}/quizzes/${response.data.quiz.id}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || '퀴즈 생성에 실패했습니다.';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">새 퀴즈 만들기</h1>
        <p className="text-neutral-600">학생들이 풀 객관식 퀴즈를 생성하세요.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <h2 className="text-xl font-bold text-neutral-800 mb-4">기본 정보</h2>

          <div className="space-y-4">
            <Input
              label="퀴즈 제목"
              name="title"
              value={formData.title}
              onChange={handleChange}
              error={errors.title}
              placeholder="예: 1단원 확인 퀴즈"
              required
            />

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                퀴즈 설명 (선택사항)
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200 min-h-[100px]"
                placeholder="퀴즈에 대한 설명..."
              />
            </div>

            <Input
              label="제한 시간 (분, 선택사항)"
              name="timeLimit"
              type="number"
              value={formData.timeLimit}
              onChange={handleChange}
              placeholder="예: 30"
              min="1"
            />
          </div>
        </Card>

        {/* Questions */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-neutral-800">문제</h2>
            <Button type="button" onClick={addQuestion} size="sm">
              문제 추가
            </Button>
          </div>

          {questions.map((q, qIndex) => (
            <Card key={qIndex} className="bg-neutral-50">
              <div className="flex justify-between items-start mb-4">
                <Badge variant="primary">문제 {qIndex + 1}</Badge>
                {questions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeQuestion(qIndex)}
                    className="text-danger-500 hover:text-danger-600 text-sm"
                  >
                    삭제
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    문제
                  </label>
                  <textarea
                    value={q.question}
                    onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200 min-h-[80px]"
                    placeholder="문제를 입력하세요..."
                  />
                  {errors[`question_${qIndex}`] && (
                    <p className="mt-1.5 text-sm text-danger-500">{errors[`question_${qIndex}`]}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    선택지
                  </label>
                  <div className="space-y-2">
                    {q.options.map((option, oIndex) => (
                      <div key={oIndex} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`correct_${qIndex}`}
                          checked={q.correctAnswer === oIndex}
                          onChange={() => updateQuestion(qIndex, 'correctAnswer', oIndex)}
                          className="w-4 h-4 text-primary-600"
                        />
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                          className="flex-1 px-4 py-2 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200"
                          placeholder={`선택지 ${oIndex + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                  {errors[`options_${qIndex}`] && (
                    <p className="mt-1.5 text-sm text-danger-500">{errors[`options_${qIndex}`]}</p>
                  )}
                </div>
              </div>
            </Card>
          ))}

          {errors.questions && (
            <p className="text-sm text-danger-500">{errors.questions}</p>
          )}
        </div>

        {errors.submit && (
          <div className="p-4 bg-danger-50 border border-danger-200 rounded-xl">
            <p className="text-sm text-danger-600">{errors.submit}</p>
          </div>
        )}

        <div className="flex gap-3">
          <Button type="submit" disabled={loading} className="flex-1">
            {loading ? '생성 중...' : '퀴즈 생성'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/courses/${courseId}`)}
            disabled={loading}
          >
            취소
          </Button>
        </div>
      </form>
    </div>
  );
}
